import {
  Input,
  Button,
  Select,
  Upload,
  success,
  useMemo,
  useEffect,
  Controller,
  useCallback,
  ErrorMessage,
  useImportData,
  useCustomForm,
  CheckBadgeIcon,
  saveImportData,
  importToOptions,
  importDataValues,
  importDataSchema,
  importFromOptions,
} from "@";
import PreviewData from "./PreviewData.jsx";
import { memo } from "react";
/**
 * form for importing data from an Excel file.
 */
export default function Form({ to, isSuccess }) {
  /**
   * Custom hook for submitting the form and saving the data.
   */
  const {
    watch,
    reset,
    errors,
    control,
    setValue,
    register,
    handleSubmit,
    isSubmitting,
    isSubmitSuccessful,
  } = useCustomForm(importDataValues, importDataSchema);

  /**
   * Custom hook for importing data from an Excel file.
   */
  const {
    uploadFile,
    previewData,
    setPreviewData,
    uploadOrderDetailsFile,
    previewDataOrderDetails,
    setPreviewDataOrderDetails,
  } = useImportData(setValue, reset, to);

  const memoizedImportToOptions = useMemo(() => importToOptions, []);
  const memoizedImportFromOptions = useMemo(() => importFromOptions, []);

  const saveImport = useCallback(
    async (data) => {
      try {
        await saveImportData(data);
        success("تم الاستيراد بنجاح");
        reset({
          file: null,
          importTo: to,
          importFrom: "",
          orderDetailsFile: null,
        });
        setPreviewData([]);
        setPreviewDataOrderDetails([]);
      } catch (error) {}
    },
    [to, reset, setPreviewData, setPreviewDataOrderDetails],
  );

  useEffect(() => {
    setValue("importTo", to);
    if (isSubmitSuccessful && typeof isSuccess === "function") {
      isSuccess();
    }
  }, [to, isSubmitSuccessful]);

  return (
    <>
      <form onSubmit={handleSubmit(saveImport)}>
        {/* Grid */}
        <div className="flex flex-col gap-5 md:flex-row">
          <div className="flex flex-col gap-5 md:flex-row">
            {/* Input */}
            {watch("importTo") !== "order" && (
              <Input
                type="select"
                style="lg:w-32 "
                value={watch("importFrom")}
                label="استيراد من"
                error={errors.importFrom}
                list={memoizedImportFromOptions}
                input={register("importFrom")}
              />
            )}

            <div className="lg:w-40">
              <label className="input-label">
                استيراد الي
                <span className="star">*</span>
              </label>
              <Controller
                name="importTo"
                control={control}
                defaultValue={to}
                render={({ field }) => {
                  const importToOpts = memoizedImportToOptions.map((o) => ({
                    value: o.value,
                    label: o.name,
                  }));
                  return (
                    <Select
                      inputId="importTo"
                      name={field.name}
                      isDisabled={!!to}
                      isClearable
                      options={importToOpts}
                      value={
                        importToOpts.find((o) => o.value === field.value) ??
                        null
                      }
                      onChange={(opt) => field.onChange(opt?.value ?? "")}
                      onBlur={field.onBlur}
                    />
                  );
                }}
              />
              <ErrorMessage value={errors.importTo?.message} />
            </div>
          </div>

          {/* Upload files */}
          <div className="flex w-full flex-col gap-5 md:flex-row">
            {/* main file */}
            <div className="w-full">
              <label className="input-label">
                {watch("importTo") === "order" ? "ملف  التبرعات " : "الملف"}
                <span className="star">*</span>
              </label>
              <Upload
                className="py-2"
                accept=".xlsx"
                upload={uploadFile}
                text={watch("file")?.name || "اضغط هنا للرفع xlsx"}
              >
                {watch("file") && (
                  <CheckBadgeIcon className="me-1 h-6 w-6 text-green-500" />
                )}
              </Upload>
              <ErrorMessage value={errors.file?.message} />
            </div>

            {/* orderDetailsFile */}
            {watch("importTo") === "order" && (
              <div className="w-full">
                <label className="input-label">
                  ملف التبرعات التفصيلي
                  <span className="star">*</span>
                </label>
                <Upload
                  className="py-2"
                  accept=".xlsx"
                  upload={uploadOrderDetailsFile}
                  text={
                    watch("orderDetailsFile")?.name || "اضغط هنا للرفع xlsx"
                  }
                >
                  {watch("orderDetailsFile") && (
                    <CheckBadgeIcon className="me-1 h-6 w-6 text-green-500" />
                  )}
                </Upload>
                <ErrorMessage value={errors.orderDetailsFile?.message} />
              </div>
            )}
          </div>
        </div>

        {/* Preview data */}
        <MemoizedPreviewData data={previewData} to={to} />
        {watch("importTo") === "order" && (
          <MemoizedPreviewData data={previewDataOrderDetails} to={to} />
        )}

        {/* Submit button */}
        <Button
          type="submit"
          className="mt-5"
          isLoading={isSubmitting}
          title="استيراد البيانات"
        />
      </form>
    </>
  );
}

const MemoizedPreviewData = memo(PreviewData);
